import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Upload, Heart, Shield, Smartphone } from 'lucide-react';

const CreatorHeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-purple-50 via-white to-pink-50 pt-20 pb-16 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
      
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          {/* HiveShare Creator Logo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img 
              src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/c79faca6-99d9-45c3-826d-610a40f2e500/public"
              alt="HiveShare Creator Logo"
              className="h-24 w-auto mx-auto mb-6"
            />
          </motion.div>

          {/* Value Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-purple-200 px-4 py-2 text-sm font-medium">
              <Heart className="h-4 w-4 mr-2 fill-current" />
              HiveShare by Hivetivity
            </Badge>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Share Your Family's{' '}
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Activity Journey
            </span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Upload, organize, and privately share videos of lessons, games, and special moments.{' '}
            <span className="font-semibold text-gray-800">No ads, no algorithms, just family memories.</span>
          </motion.p>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-6 mb-10 text-sm text-gray-500"
          >
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-600" />
              <span>Private & Secure</span>
            </div>
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-blue-600" />
              <span>Mobile First</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="h-4 w-4 text-red-500 fill-current" />
              <span>Built for Families</span>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
          >
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start Creating for $9/month
              <Upload className="h-5 w-5 ml-2" />
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              className="border-2 border-purple-200 text-purple-700 hover:bg-purple-50 px-8 py-4 text-lg font-semibold"
            >
              <Play className="h-5 w-5 mr-2" />
              See How It Works
            </Button>
          </motion.div>

          {/* Pricing Highlight */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 max-w-2xl mx-auto border border-purple-100"
          >
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">$9</div>
                <div className="text-sm text-gray-600">per month</div>
              </div>
              <div className="text-gray-400 text-2xl">vs</div>
              <div className="text-center">
                <div className="text-lg text-gray-400 line-through">$20</div>
                <div className="text-xs text-gray-500">other platforms</div>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              💳 No credit card required for trial • ✨ Cancel anytime • 🔒 Your videos stay private
            </p>
          </motion.div>
        </div>

        {/* Hero Image/Video Placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-16 max-w-5xl mx-auto"
        >
          <div className="relative bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl shadow-2xl aspect-video flex items-center justify-center">
            <div className="text-center">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-full p-6 mb-4 inline-block">
                <Play className="h-12 w-12 text-white" />
              </div>
              <p className="text-lg font-semibold text-gray-700">See HiveShare Creator in Action</p>
              <p className="text-gray-500">Watch how easy it is to share your family's moments</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CreatorHeroSection;